import React, { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DevopsHubHeader from "../components/devops/DevopsHubHeader";
import DevopsEntityCard from "../components/devops/DevopsEntityCard";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import type { DevopsEnvironment } from "../types/devops";
import { canMaintainEnvironment } from "../utils/devopsAccess";
import { devopsRoutes } from "../utils/devopsRoutes";

const EnvironmentsHubPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const roles = user?.roles ?? [];
  const { bindEntity } = useDevopsWorkspace();
  const [items, setItems] = useState<DevopsEnvironment[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canWrite = canMaintainEnvironment(roles);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const envs = await devopsService.listEnvironments();
        if (!cancelled) setItems(envs);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load environments");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      [item.name, item.purpose ?? "", item.owner, item.description ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [items, search]);

  return (
    <>
      <DevopsHubHeader
        title="Environments"
        description="Runtime environments, platform services, and env-level ownership. Environment managers can add and edit."
        search={search}
        onSearchChange={setSearch}
        onAdd={canWrite ? () => navigate(devopsRoutes.environmentNew) : undefined}
        addLabel="Add environment"
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Grid container spacing={2}>
          {filtered.map((item) => (
            <Grid item xs={12} sm={6} key={item.uuid}>
              <DevopsEntityCard
                title={item.name}
                subtitle={item.purpose ?? item.description ?? undefined}
                status={item.status}
                metadata={
                  <Typography variant="caption" color="text.secondary">
                    Owner {item.owner} · {(item.applications ?? []).length} app
                    {(item.applications ?? []).length === 1 ? "" : "s"}
                  </Typography>
                }
                onView={() => navigate(devopsRoutes.environmentView(item.uuid))}
                onAskAgent={() =>
                  bindEntity({ type: "ENVIRONMENT", uuid: item.uuid, name: item.name })
                }
                onEdit={canWrite ? () => navigate(devopsRoutes.environmentEdit(item.uuid)) : undefined}
                onDelete={
                  canWrite
                    ? async () => {
                        if (!window.confirm(`Delete environment ${item.name}?`)) return;
                        await devopsService.deleteEnvironment(item.uuid);
                        setItems((prev) => prev.filter((row) => row.uuid !== item.uuid));
                      }
                    : undefined
                }
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No environments match your search.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default EnvironmentsHubPage;
